import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { IconButton, Stack } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo, useRef } from "react";
import IwMenu from "../../components/app/IwMenu";
import {
  CALENDAR_COMPONENT_STATIC_TEXTS,
  CALENDAR_SWITCH_VALUES
} from "../../constants";
import { baseAtom } from "../../stores/jotai/base";
import { useGetZoomMeetings } from "../../tanstackQuery/social-integrations/handle-zoom";
import { useDisconnectLogin } from "../../tanstackQuery/social-integrations/useDisconnectLogin";
import { useUserAuthStatus } from "../../tanstackQuery/social-integrations/useUserAuthStatus";
import CalendarFilterSwitch, { selectedTabAtom } from "./CalendarFilterSwitch";
import CalendarFrame from "./CalendarFrame";
import MeetingList from "./GoogleAndZoom/MeetingList";
import HomeSegmentCard from "./HomeSegmentCard";
import HomeSegmentContent from "./HomeSegmentContent";
import HomeSegmentTitle from "./HomeSegmentTitle";
import { zoomIntegrationStateAtom } from "../../stores/jotai/calender&Zoom";

const CalendarSegmentCard = () => {
  const selectedTab= useAtomValue(selectedTabAtom);
  const base = useAtomValue(baseAtom);
  const { keycloak } = useKeycloak();

  const setZoomIntegration = useSetAtom(zoomIntegrationStateAtom);

  const client = useQueryClient();
  const {
    data: authStatus,
    isLoading,
    isRefetching,
    isFetching
  } = useUserAuthStatus(
    { accessToken: keycloak.token },
    { userId: base?.user?.userID, teamId: base?.team?.id },
    //@ts-ignore
    // {refetchOnWindowFocus: false}
    { enabled: !!(base?.user?.userID && base?.team?.id) }
  );

  const {
    data: zoomMeetings,
    isLoading: isMeetingLoading,
    refetch: zoomMeetingRefetch,
    isRefetching: isMeetingRefetching
  } = useGetZoomMeetings(
    { accessToken: keycloak.token },
    { appTeamID: base?.team?.groupID },
    // @ts-ignore
    { enabled: !!base?.team?.groupID }
  );

  const { mutate: disconnect } = useDisconnectLogin(
    { accessToken: keycloak?.token },
    {
      onSuccess() {
        client.invalidateQueries({ queryKey: ["user-auth-status"] });
        client.invalidateQueries({ queryKey: ["zoom-meetings"] });
      }
    }
  );

  const menuRef = useRef<{ closeMenu: () => void }>(null);

  const closeMenuFromParent = () => {
    if (menuRef.current) {
      menuRef.current.closeMenu();
    }
  };

  const menuItems = useMemo(() => {
    const _menuItems = [];

    if (authStatus?.isGoogleLoggedIn) {
      _menuItems.push({
        id: "1",
        label: "Disconnect from Calendar",
        tooltipProps: {
          title: "Disconnect from Calendar"
        },
        suffix: <EventBusyOutlinedIcon fontSize="small" />,
        onClick: async () => {
          disconnect({
            authType: "google",
            teamId: base?.team?.id,
            userId: base?.user?.userID
          });

          setZoomIntegration((prev) => {
            return {
              ...prev,
              googleMessageView: false,
              zoomMessageView: false,
              zoomPermissionErrorView: false,
              zoomLicesePermisson:false,
              errorMessage:null
            };
          });

          closeMenuFromParent();
        }
      });
    }

    if (authStatus?.isZoomLoggedIn) {
      _menuItems.push({
        id: "2",
        label: "Disconnect from Zoom",
        tooltipProps: {
          title: "Disconnect from Zoom"
        },
        suffix: <LogoutIcon fontSize="small" />,
        onClick: async () => {
          disconnect({
            authType: "zoom",
            teamId: base?.team?.id,
            userId: base?.user?.userID
          });

          setZoomIntegration((prev) => {
            return {
              ...prev,
              googleMessageView: false,
              zoomMessageView: false,
              zoomPermissionErrorView: false,
              zoomLicesePermisson:false,
              errorMessage:null
            };
          });

          closeMenuFromParent();
        }
      });
    }

    return _menuItems;
  }, [authStatus]);

  return (
    <HomeSegmentCard>
      <HomeSegmentTitle>
        <CalendarMonthIcon /> {CALENDAR_COMPONENT_STATIC_TEXTS.POPOVER.TITLE}
        <Stack
          sx={{ marginLeft: "auto" }}
          direction="row"
          alignItems="center"
          gap={0.5}
        >
          {(selectedTab === CALENDAR_SWITCH_VALUES.MEETING_LIST ||
            authStatus?.isGoogleLoggedIn ||
            authStatus?.isZoomLoggedIn) && (
            <IconButton
              onClick={() => {
                if (selectedTab === CALENDAR_SWITCH_VALUES.MEETING_LIST) {
                  setZoomIntegration((prev) => ({
                    ...prev,
                    eventsRefetching: false,
                    eventsRefetchingWithPubnub: false,
                    meetingListRefetching: true
                  }));

                  return;
                }

                setZoomIntegration((prev) => ({
                  ...prev,
                  eventsRefetching: true
                }));
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: "24px"
                }}
              />
            </IconButton>
          )}

          {(authStatus?.isGoogleLoggedIn || authStatus?.isZoomLoggedIn) && (
            <>
              <IwMenu
                // eslint-disable-next-line
                // @ts-ignore
                ref={menuRef}
                label={<SettingsOutlinedIcon />}
                tooltipProps={{
                  title: "Calendar Options"
                }}
                triggerComponentSx={{
                  margin: "0 8px",
                  padding: "2px 0"
                }}
                items={menuItems}
              />
            </>
          )}
          <CalendarFilterSwitch />
        </Stack>
      </HomeSegmentTitle>
      <HomeSegmentContent>
        {selectedTab === CALENDAR_SWITCH_VALUES.CALENDAR ? (
          <CalendarFrame
            authStatus={authStatus}
            isAuthLoading={isLoading || isFetching || isRefetching}
          />
        ) : (
          <MeetingList
            zoomMeetings={zoomMeetings}
            isMeetingLoading={isMeetingLoading}
            isMeetingRefetching={isMeetingRefetching}
            meetingRefetch={zoomMeetingRefetch}
          />
        )}
      </HomeSegmentContent>
    </HomeSegmentCard>
  );
};

export default CalendarSegmentCard;
